import * as React from "react";
import { API } from "../../API";
import useCaregiver from "../../hooks/useCaregiver";
import useMixin from "../../hooks/useMixin";
import { isLoading } from "../../Loading";
import { AgencyId, AgencyMemberId } from "../../messages";
import { STRINGS } from "../../strings";
import { AppState } from "../../system/AppState";
import { translate } from "../../translate";
import { getAnomalies } from "../CaseSettings/utils/getAnomalies";
import LoadingScaffold from "../LoadingScaffold";
import { simpleAlert } from "../SimpleAlert";
import MyAgenciesScreen from "./MyAgenciesScreen";
import { cancelAgencyRequest } from "./utils/cancelAgencyRequest";
import sendRecruitEmail from "./utils/sendRecruitEmai";

interface MyAgenciesActivityProps {
  api: API;
  appState: AppState;
  onClickAddAgency: () => void;
  onClickChatWithAgencyMember: (agencyMemberId: AgencyMemberId) => void;
  onClickChatWithMedflytAgency: () => void;
  onClickGoBack: () => void;
}

const MyAgenciesActivity = (props: MyAgenciesActivityProps) => {
  const mixin = useMixin(props.appState);
  const caregiver = useCaregiver(props.appState);
  const [isWorkingWithAgencyBottomSheetOpen, setWorkingWithAgencyBottomSheetOpen] =
    React.useState(false);

  const getAnomaliesCallback = React.useCallback(() => {
    return getAnomalies(props.api);
  }, [props.api]);

  const handleRequestCloseBottomSheet = React.useCallback(() => {
    return setWorkingWithAgencyBottomSheetOpen(false);
  }, []);

  const handleClickRequestAgencyMembership = React.useCallback(() => {
    return setWorkingWithAgencyBottomSheetOpen(true);
  }, []);

  const handleClickWorkingWithAnAgency = React.useCallback(() => {
    setWorkingWithAgencyBottomSheetOpen(false);
    props.onClickAddAgency.call(null);
  }, [props.onClickAddAgency]);

  const handleClickNotWorkingWithAnAgency = React.useCallback(async () => {
    if (!isLoading(mixin.appAgenciesState) && !isLoading(caregiver)) {
      const epilogue = await getAnomaliesCallback();
      sendRecruitEmail(caregiver, mixin.appAgenciesState.agencies.values(), epilogue);

      setTimeout(() => {
        simpleAlert({
          icon: "success",
          message: translate(STRINGS.WELL_BE_IN_TOUCH_SHORTLY),
        });
      }, 3000);
    }
  }, [caregiver, mixin.appAgenciesState, getAnomaliesCallback]);

  const handleClickCancelAgency = React.useCallback(
    (agencyId: AgencyId) => {
      return cancelAgencyRequest(agencyId, props.api, props.appState);
    },
    [props.api, props.appState]
  );

  if (isLoading(mixin.appAgenciesState) || isLoading(caregiver)) {
    return (
      <LoadingScaffold title={translate(STRINGS.MY_AGENCIES)} onClickGoBack={props.onClickGoBack} />
    );
  }

  return (
    <MyAgenciesScreen
      agencies={mixin.appAgenciesState.agencies}
      caregiver={caregiver}
      isWorkingWithAgencyBottomSheetOpen={isWorkingWithAgencyBottomSheetOpen}
      onRequestCloseBottomSheet={handleRequestCloseBottomSheet}
      onClickRequestAgencyMembership={handleClickRequestAgencyMembership}
      onClickWorkingWithAnAgency={handleClickWorkingWithAnAgency}
      onClickNotWorkingWithAnAgency={handleClickNotWorkingWithAnAgency}
      onClickAddAgency={props.onClickAddAgency}
      onClickCancelAgency={handleClickCancelAgency}
      onClickChatWithAgencyMember={props.onClickChatWithAgencyMember}
      onClickChatWithMedflytAgency={props.onClickChatWithMedflytAgency}
      onClickGoBack={props.onClickGoBack}
    />
  );
};

export default React.memo(MyAgenciesActivity);
